import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  FormControlLabel, Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const BankModal = ({ open, onClose, onSave, bankData = null, mode = 'create', existingBanks = [] }) => {
  const { companyId, userId, token, roleName } = useAuth();
  const isAdminRole = roleName === 'CompanyAdmin' || roleName === 'SuperAdmin';
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const isCompany2 = Number(companyId) === 2;

  const [formData, setFormData] = useState({
    Name: '',
    IFSCCode: '',
    AccountNumber: '',
    OpeningBalance: '',
    IsPayable: false,
    OpeningBalance2: '',
    IsPayable2: false,
    IsActive: true,
    IsAdminView: false,
  });
  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (bankData && mode !== 'create') {
        setFormData({
          Id: bankData.Id || '',
          Name: bankData.Name || '',
          IFSCCode: bankData.IFSCCode || '',
          AccountNumber: bankData.AccountNumber || '',
          OpeningBalance: bankData.OpeningBalance ?? '',
          IsPayable: bankData.IsPayable ?? false,
          OpeningBalance2: bankData.OpeningBalance2 ?? '',
          IsPayable2: bankData.IsPayable2 ?? false,
          IsActive: bankData.IsActive ?? true,
          IsAdminView: bankData.IsAdminView ?? false,
        });
      } else {
        setFormData({
          Name: '',
          IFSCCode: '',
          AccountNumber: '',
          OpeningBalance: '',
          IsPayable: false,
          OpeningBalance2: '',
          IsPayable2: false,
          IsActive: true,
          IsAdminView: false,
        });
      }
      setErrors({});
    }
  }, [open, bankData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name?.trim()) newErrors.Name = 'Name is required';
    if (formData.IFSCCode && formData.IFSCCode.length > 30)
      newErrors.IFSCCode = 'IFSC Code must be 30 characters or less';
    if (formData.AccountNumber && formData.AccountNumber.length > 30)
      newErrors.AccountNumber = 'Account Number must be 30 characters or less';
    if (isCompany2) {
      if (formData.OpeningBalance2 === '' || formData.OpeningBalance2 === null) {
        newErrors.OpeningBalance2 = 'Opening Balance is required';
      } else if (isNaN(formData.OpeningBalance2)) {
        newErrors.OpeningBalance2 = 'Enter a valid amount';
      }
    } else {
      if (formData.OpeningBalance === '' || formData.OpeningBalance === null) {
        newErrors.OpeningBalance = 'Opening Balance is required';
      } else if (isNaN(formData.OpeningBalance)) {
        newErrors.OpeningBalance = 'Enter a valid amount';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => existingBanks.find(b => {
    if (mode === 'edit' && bankData && b.Id === bankData.Id) return false;
    return b.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
  });

  const handleSave = async () => {
    if (!validateForm()) return;
    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(`A Bank with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }
    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        IFSCCode: formData.IFSCCode || null,
        AccountNumber: formData.AccountNumber || null,
        OpeningBalance: isCompany2 ? 0 : (parseFloat(formData.OpeningBalance) || 0),
        IsPayable: isCompany2 ? false : formData.IsPayable,
        OpeningBalance2: isCompany2 ? (parseFloat(formData.OpeningBalance2) || 0) : 0,
        IsPayable2: isCompany2 ? formData.IsPayable2 : false,
        IsActive: formData.IsActive,
        IsAdminView: formData.IsAdminView,
        CompanyId: companyId,
      };
      if (mode === 'edit') {
        if (!bankData || !bankData.Id) { alert('Error: Cannot update Bank - missing ID'); return; }
        dataToSend.Id = bankData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (bankData.CreatedBy) dataToSend.CreatedBy = bankData.CreatedBy;
        if (bankData.CreatedDate) dataToSend.CreatedDate = bankData.CreatedDate;
        await updateData('BankApi', bankData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('BankApi','', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Bank:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => ({ create: 'New Bank', edit: 'Edit Bank', view: 'Bank Details' }[mode] || 'Bank');
  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            padding: '2px', margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', height: isMobile ? '100vh' : 'auto' }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            color: 'white', padding: '8px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AccountBalanceIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}>
              {mode === 'create' ? 'Add a new Bank to your system' : mode === 'edit' ? 'Update Bank information' : 'View Bank details'}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}>
                  Bank Information
                </Typography>
              </Grid>

              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  name="Name" label="Name *" value={formData.Name} onChange={handleChange}
                  fullWidth size="small" error={!!errors.Name} helperText={errors.Name}
                  disabled={isReadOnly} placeholder="Enter bank name" inputProps={{ maxLength: 50 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* IFSC Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="IFSCCode" label="IFSC Code" value={formData.IFSCCode} onChange={handleChange}
                  fullWidth size="small" error={!!errors.IFSCCode} helperText={errors.IFSCCode}
                  disabled={isReadOnly} placeholder="e.g. SBIN0001234" inputProps={{ maxLength: 30 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Account Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="AccountNumber" label="Account Number" value={formData.AccountNumber} onChange={handleChange}
                  fullWidth size="small" error={!!errors.AccountNumber} helperText={errors.AccountNumber}
                  disabled={isReadOnly} placeholder="Enter account number" inputProps={{ maxLength: 30 }}
                  InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Opening Balance */}
              <Grid item xs={12} sm={6}>
                {isCompany2 ? (
                  <TextField
                    name="OpeningBalance2" label="Opening Balance *" value={formData.OpeningBalance2} onChange={handleChange}
                    fullWidth size="small" type="number" error={!!errors.OpeningBalance2} helperText={errors.OpeningBalance2}
                    disabled={isReadOnly} placeholder="0.00" inputProps={{ step: '0.01' }}
                    InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                    FormHelperTextProps={{ style: helperStyle }}
                  />
                ) : (
                  <TextField
                    name="OpeningBalance" label="Opening Balance *" value={formData.OpeningBalance} onChange={handleChange}
                    fullWidth size="small" type="number" error={!!errors.OpeningBalance} helperText={errors.OpeningBalance}
                    disabled={isReadOnly} placeholder="0.00" inputProps={{ step: '0.01' }}
                    InputProps={{ style: inputStyle }} InputLabelProps={{ style: labelStyle }}
                    FormHelperTextProps={{ style: helperStyle }}
                  />
                )}
              </Grid>

              {/* Toggles */}
              <Grid item xs={6} sm={3}>
                {isCompany2 ? (
                  <FormControlLabel
                    control={<Switch checked={formData.IsPayable2} onChange={handleChange} name="IsPayable2" disabled={isReadOnly} size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                    label={<Typography style={{ fontSize: '11px' }}>Is Payable</Typography>}
                  />
                ) : (
                  <FormControlLabel
                    control={<Switch checked={formData.IsPayable} onChange={handleChange} name="IsPayable" disabled={isReadOnly} size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                    label={<Typography style={{ fontSize: '11px' }}>Is Payable</Typography>}
                  />
                )}
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsActive} onChange={handleChange} name="IsActive" disabled={isReadOnly} size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Active</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Switch checked={formData.IsAdminView} onChange={handleChange} name="IsAdminView" disabled={isReadOnly || !isAdminRole} size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#5c3d1e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5c3d1e' } }} />}
                  label={<Typography style={{ fontSize: '11px' }}>Is Admin View</Typography>}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto' }}>
            <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button onClick={handleSave} disabled={loading} variant="contained"
                style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 12px', fontSize: '12px' }}>
                {loading ? 'Saving...' : mode === 'create' ? 'Add Bank' : 'Update Bank'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate warning dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}>
            Duplicate Bank Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>{duplicateMessage}</Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} variant="contained"
            style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '8px 32px', fontSize: '14px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BankModal;
